"use client";

import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getOffer } from "@/api/simpego";
import { useErrorDialog } from "@/context/ErrorDialogContext";
import { OfferResponse } from "@/types/simpego-api";

interface UseOfferLoaderProps {
  setFunnelStateFromOffer: (offer: OfferResponse, agentIdOverride?: string | null) => string; // Now returns the target path key
  handleResetFunnel: () => void;
  setIsOfferLoadingFromUrl: (isLoading: boolean) => void;
  currentLocale: string;
}

const normalizeAgentId = (raw: string | null) => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const agentWithoutPrefix = trimmed.replace(/^va/i, "");
  const digitsOnly = agentWithoutPrefix.replace(/\D/g, "");
  if (!digitsOnly) return null;

  const padded = digitsOnly.padStart(10, "0");
  return `VA${padded}`;
};

export const useOfferLoader = ({
  setFunnelStateFromOffer,
  handleResetFunnel,
  setIsOfferLoadingFromUrl,
  currentLocale,
}: UseOfferLoaderProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showErrorDialog } = useErrorDialog();
  const [isInitialLoad, setIsInitialLoad] = React.useState<boolean>(true);

  React.useEffect(() => {
    // Parse query parameters from the hash fragment
    const hashQuery = location.search; // For HashRouter, location.search contains the query string from the hash
    const searchParams = new URLSearchParams(hashQuery);
    const urlContractId = searchParams.get("contractID");
    const urlContractToken = searchParams.get("contractToken");
    const urlAgentId = searchParams.get("agentID"); // New: Get agentID from URL
    const normalizedDirectAgent = normalizeAgentId(searchParams.get("agent"));
    const resolvedAgentId = normalizedDirectAgent ?? urlAgentId;

    if (urlContractId && urlContractToken && isInitialLoad) {
      setIsInitialLoad(false);
      const loadOffer = async () => {
        setIsOfferLoadingFromUrl(true);
        try {
          console.log("Attempting to load offer with:", { urlContractToken, urlContractId, resolvedAgentId });

          const offerResponse = await getOffer(urlContractToken, urlContractId);

          // Redirect externally when the offer has already been applied
          const isApplied = offerResponse.status?.toLowerCase() === "applied";
          const appliedRedirectUrl = i18n.getResource(currentLocale, "translation", "externalLinks.appliedOffer") as string | undefined;
          if (isApplied && appliedRedirectUrl) {
            window.location.href = appliedRedirectUrl;
            return;
          }

          // IMPORTANT: Ensure i18n language is set to the URL's locale before processing the offer
          if (i18n.language !== currentLocale) {
            console.log(`useOfferLoader: Changing i18n language from ${i18n.language} to ${currentLocale}`);
            await i18n.changeLanguage(currentLocale);
          }

          const routeKey = setFunnelStateFromOffer(offerResponse, resolvedAgentId); // Get route key like "routes.confirmation"
          
          // Prepend the current locale to the target path
          // Use i18n.getResource to explicitly get the translation for the currentLocale
          const baseRoute = i18n.getResource(currentLocale, 'translation', routeKey) as string;
          const localizedTargetPath = `/${currentLocale}${baseRoute.startsWith('/') ? baseRoute : `/${baseRoute}`}`;
          navigate(localizedTargetPath, { replace: true, state: { fromOfferLoad: true } }); // Added fromOfferLoad flag
          
          // Manually clear the contractID, contractToken, and agentID from the hash query
          const currentHash = window.location.hash;
          const [hashPath, existingHashQuery] = currentHash.split('?');
          const existingSearchParams = new URLSearchParams(existingHashQuery);
          
          let updated = false;
          if (existingSearchParams.has('contractID')) {
            existingSearchParams.delete('contractID');
            updated = true;
          }
          if (existingSearchParams.has('contractToken')) {
            existingSearchParams.delete('contractToken');
            updated = true;
          }
          ['agentID', 'agent'].forEach((param) => {
            if (existingSearchParams.has(param)) {
              existingSearchParams.delete(param);
              updated = true;
            }
          });
          if (existingSearchParams.has('wl')) {
            existingSearchParams.delete('wl');
            updated = true;
          }

          if (updated) {
            const newHashQuery = existingSearchParams.toString();
            const newHash = `${hashPath}${newHashQuery ? `?${newHashQuery}` : ''}`;
            window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}${newHash}`);
            console.log("useOfferLoader - Removed contractID, contractToken, agent/agentID, and wl from URL hash.");
          }

        } catch (error) {
          console.error("Failed to load offer from URL:", error);
          showErrorDialog(t("common.errorLoadingOffer"));
          handleResetFunnel();
          navigate(`/${currentLocale}${i18n.getResource(currentLocale, 'translation', 'routes.home')}`, { replace: true }); // Navigate to localized home on error
        } finally {
          setIsOfferLoadingFromUrl(false); // Set immediately
        }
      };
      loadOffer();
    }
  }, [location.search, isInitialLoad, setFunnelStateFromOffer, t, handleResetFunnel, navigate, setIsOfferLoadingFromUrl, currentLocale, i18n, showErrorDialog]);
};
